import React, { useEffect, useRef, useState } from 'react'

import { AxiosError } from 'axios'
import {
  Button,
  CheckBox,
  Col,
  Grid,
  Input,
  InputMask,
  Picker,
  Row,
  Span
} from 'components'
import { useModal, useLoading } from 'hooks'
import Masker from 'hooks/useMasker'
import moment from 'moment'
import { ContractServices, ResponseContractById } from 'services/NGT/Contract'
import {
  CaixaServices,
  ICaixaResponseDto,
  DocumentoCaixaServices,
  ICaixaForm,
  TipoCaixa,
  ICaixaModel
} from 'services/NGT/Files'
import { FONTS, SPACING, TYPOGRAPHY, FORMAT } from 'utils'

const DEFAULT_VALUES_ICAIXAFORM: ICaixaForm = {
  caixaDepartamental: {
    idCaixa: 0,
    idTipoCaixa: 0,
    numeroCaixa: ''
  },
  caixaExterna: {
    idCaixa: 0,
    idTipoCaixa: 0,
    numeroCaixa: ''
  },
  cdContrato: '',
  cnpj: '',
  competencia: moment(),
  currentCaixa: TipoCaixa.Externa,
  descricao: '',
  nmCentro: '',
  nmEmpresa: '',
  nmFornecedor: ''
}

interface IEditNewDocumentationProps {
  documentoCaixaId: number
  handleChange: (_x: any, _newValue: number) => void
}

interface ICaixa {
  externa: ICaixaResponseDto
  departamental: ICaixaResponseDto
}

const EditNewDocumentation: React.FC<IEditNewDocumentationProps> = ({
  documentoCaixaId,
  handleChange
}) => {
  const { setLoading } = useLoading()
  const { showModal } = useModal()

  const [caixa, setCaixa] = useState<ICaixa>({
    departamental: {
      flCaixaAtual: false,
      idCaixa: 0,
      idCentroPlanta: 0,
      idTipoCaixa: TipoCaixa.Departamental,
      nuAnoMesCompetencia: '',
      nuCaixa: 0
    },
    externa: {
      flCaixaAtual: false,
      idCaixa: 0,
      idCentroPlanta: 0,
      idTipoCaixa: TipoCaixa.Externa,
      nuAnoMesCompetencia: '',
      nuCaixa: 0
    }
  })
  const [contract, setContract] = useState<ResponseContractById | undefined>()
  const [form, setForm] = useState<ICaixaForm>(DEFAULT_VALUES_ICAIXAFORM)
  const incrementValue = useRef<number>(0)
  const [firstMount, setFirstMount] = useState<boolean>(true)

  const currentCaixa = (): ICaixaModel =>
    form.currentCaixa == TipoCaixa.Externa
      ? form.caixaExterna
      : form.caixaDepartamental

  const onSubmit = () => {
    if (!contract && form.currentCaixa === TipoCaixa.Departamental) {
      showModal({
        hideButtonCancel: true,
        message: 'Antes você deve informar o contrato',
        txtButtonConfirm: 'OK',
        type: 'alert'
      })
      return
    }

    setLoading('loading')

    DocumentoCaixaServices()
      .update({
        idCaixa: currentCaixa()?.idCaixa,
        idContrato: contract.idContrato,
        idDocumentoCaixa: form.idDocumentoCaixa,
        nuAnoMesCompetencia: moment(form?.competencia)?.format('yyyyMM'),
        txDescricaoCaixa: form.descricao || ''
      })
      .then((response) => {
        setLoading('idle')
        if (response.status === 200) {
          clearForm()
          showModal({
            hideButtonCancel: true,
            message: 'Dados atualizados com sucesso',
            txtButtonConfirm: 'OK',
            type: 'success'
          })

          handleChange(null, 0)
          setCaixa(response.data.entity)
        }
      })
      .catch(() => {
        setLoading('idle')

        showModal({
          hideButtonCancel: true,
          message: 'Não foi possível salvar o documento',
          txtButtonConfirm: 'OK',
          type: 'error'
        })
      })
  }

  const handleGetContractError = () =>
    showModal({
      hideButtonCancel: true,
      message: 'Não foi possível obter os dados do contrato',
      txtButtonConfirm: 'OK',
      type: 'error'
    })

  const handleCaixaError = () =>
    showModal({
      hideButtonCancel: true,
      message: 'Não foi possível obter os dados da caixa',
      txtButtonConfirm: 'OK',
      type: 'error'
    })

  const clearForm = () => {
    setContract(undefined)
    setCaixa(undefined)
    setForm((prevState) => ({
      ...DEFAULT_VALUES_ICAIXAFORM,
      caixaExterna: {
        ...prevState.caixaExterna
      }
    }))
  }

  const getContractById = (idContract: number) => {
    ContractServices()
      .GetContractById(idContract)
      .then((response) => {
        if (response.status === 200 && !Array.isArray(response.data)) {
          const { data: contract } = response
          setForm((prevState) => ({
            ...prevState,
            cdContrato: contract.cdContrato,
            cnpj: Masker(
              contract?.cnpj?.trim(),
              isNaN(parseInt(contract?.cnpj))
                ? ['NN###########']
                : ['###.###.###-##', '##.###.###/####-##']
            ),
            nmCentro: contract?.nmCentro?.trim(),
            nmEmpresa: contract?.nmEmpresa?.trim(),
            nmFornecedor: contract?.nmFornecedor?.trim()
          }))
          setContract(contract)
          setLoading('idle')
        } else {
          setLoading('idle')
          setContract(undefined)
          clearForm()
          handleGetContractError()
        }
      })
      .catch(() => {
        setLoading('idle')
        setContract(undefined)
        handleGetContractError()
      })
  }

  const getDocumentoCaixaById = () => {
    setLoading('loading')

    DocumentoCaixaServices()
      .getById(documentoCaixaId)
      .then(({ data, status }) => {
        if (status === 200) {
          setForm((prevState) => ({
            ...prevState,
            competencia: data.competencia,
            currentCaixa: data.isCaixaExterno
              ? TipoCaixa.Externa
              : TipoCaixa.Departamental,
            descricao: data.descricao,
            idDocumentoCaixa: data.idDocumentoCaixa,
            [data.isCaixaExterno ? 'caixaExterna' : 'caixaDepartamental']: {
              idCaixa: data.idCaixa,
              idTipoCaixa: data.isCaixaExterno ? 1 : 2,
              numeroCaixa: data.nuCaixa
            }
          }))

          setCaixa((oldData) => ({
            ...oldData,
            [data.isCaixaExterno ? 'externa' : 'departamental']: {
              flCaixaAtual: data.flCaixaAtual,
              idCaixa: data.idCaixa,
              idCentroPlanta: data.idCentroPlanta,
              idTipoCaixa: data.isCaixaExterno ? 1 : 2,
              nuAnoMesCompetencia: data?.competencia,
              nuCaixa: data.nuCaixa
            }
          }))

          getContractById(data.idContrato)
        }
      })
  }

  const handleSelectCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    const tipoCaixa: TipoCaixa =
      event.target.name === 'caixaDepartamental'
        ? TipoCaixa.Departamental
        : TipoCaixa.Externa

    if (event.target.name === 'caixaDepartamental' && !contract) {
      showModal({
        hideButtonCancel: true,
        message:
          'Antes de marcar a caixa departamental você precisa buscar por um contrato',
        txtButtonConfirm: 'OK',
        type: 'alert'
      })
      return
    }

    setForm((prevState) => ({
      ...prevState,
      currentCaixa: tipoCaixa
    }))
  }

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // eslint-disable-next-line prefer-const
    let [section, key] = event.target.name.split('.')

    if (section === 'currentCaixa' && key) {
      section =
        form.currentCaixa === TipoCaixa.Externa
          ? 'caixaExterna'
          : 'caixaDepartamental'
    }

    if (key) {
      setForm((prevState) => ({
        ...prevState,
        [section]: {
          ...prevState[section],
          [key]: event.target.value
        }
      }))
    } else {
      setForm((prevState) => ({
        ...prevState,
        [event.target.name]: event.target.value
      }))
    }
  }

  const validadeCaixaDepartamentalForm = (tipoCaixa: TipoCaixa) => {
    const isValid = true
    const isNotValid = false

    const caixaSelecionada: ICaixaModel =
      tipoCaixa === TipoCaixa.Externa
        ? form.caixaExterna
        : form.caixaDepartamental

    if (
      !contract?.idContrato &&
      form.currentCaixa === TipoCaixa.Departamental
    ) {
      showModal({
        hideButtonCancel: true,
        message: 'Antes você deve informar o contrato',
        txtButtonConfirm: 'OK',
        type: 'alert'
      })
      return isNotValid
    }

    if (!form.competencia) {
      showModal({
        hideButtonCancel: true,
        message: 'Antes você deve informar a competência',
        txtButtonConfirm: 'OK',
        type: 'alert'
      })
      return isNotValid
    }

    if (!caixaSelecionada.numeroCaixa) {
      showModal({
        hideButtonCancel: true,
        message: 'Antes você deve informar o número da caixa',
        txtButtonConfirm: 'OK',
        type: 'alert'
      })
      return isNotValid
    }

    return isValid
  }

  const showModalAbrirCaixa = (tipoCaixa: TipoCaixa) => {
    showModal({
      handleCancel: () => incrementCaixa(tipoCaixa, false),
      handleConfirm: () => incrementCaixa(tipoCaixa, true),
      message: 'Devo considerar nova numeração como próxima sequência (S/N)?',
      txtButtonCancel: 'NÃO',
      txtButtonConfirm: 'SIM',
      type: 'alert'
    })
  }

  const incrementCaixa = (idTipoCaixa: TipoCaixa, isAbrirCaixa?: boolean) => {
    setLoading('loading')

    const caixaAtualSelecionada = currentCaixa()

    CaixaServices()
      .create({
        flCaixaAtual: false,
        idCentroPlanta: contract?.centroPlantaId,
        idTipoCaixa: idTipoCaixa,
        isAbrirCaixa: isAbrirCaixa,
        nuCaixa:
          Number(caixaAtualSelecionada.numeroCaixa) + incrementValue.current
      })
      .then(({ data, status }) => {
        const { valor } = data

        if (status === 200 && valor) {
          setForm((prevState) => ({
            ...prevState,
            [form.currentCaixa === TipoCaixa.Externa
              ? 'caixaExterna'
              : 'caixaDepartamental']: {
              idCaixa: valor.idCaixa,
              idTipoCaixa: valor.idTipoCaixa,
              numeroCaixa: valor.nuCaixa
            }
          }))

          setCaixa((oldData) => ({
            ...oldData,
            [idTipoCaixa === TipoCaixa.Externa ? 'externa' : 'departamental']:
              valor
          }))

          setLoading('idle')
          incrementValue.current = 0
        }
      })
      .catch((error) => {
        const axiosError = error as AxiosError
        incrementValue.current = 0
        setLoading('idle')

        const responseError = axiosError.response?.data.detail
          ? error.response.data.detail
          : ' '

        showModal({
          hideButtonCancel: true,
          message: `Não foi possível gerar a caixa. ${responseError}`,
          txtButtonConfirm: 'OK',
          type: 'error'
        })
      })
  }

  const gerarCaixaExterno = () => {
    if (!contract && form.currentCaixa === TipoCaixa.Departamental) {
      showModal({
        hideButtonCancel: true,
        message: 'Primeiro você deve digitar o número do contrato.',
        txtButtonConfirm: 'OK',
        type: 'alert'
      })
      return
    }

    if (!validadeCaixaDepartamentalForm(form.currentCaixa)) {
      return
    }

    if (
      caixa?.externa.nuCaixa + 1 < Number(form.caixaExterna.numeroCaixa) ||
      caixa?.externa.nuCaixa > Number(form.caixaExterna.numeroCaixa)
    ) {
      showModalAbrirCaixa(TipoCaixa.Externa)
      return
    }

    if (caixa?.externa.nuCaixa === Number(form.caixaExterna.numeroCaixa)) {
      setForm((prevState: ICaixaForm) => ({
        ...prevState,
        caixaExterna: {
          ...prevState.caixaExterna,
          numeroCaixa: (caixa?.externa.nuCaixa + 1).toString()
        }
      }))
      incrementValue.current = 1
    }

    incrementCaixa(TipoCaixa.Externa, true)
  }

  const gerarCaixaDepartamental = () => {
    if (!validadeCaixaDepartamentalForm(form.currentCaixa)) {
      return
    }

    if (!caixa) {
      incrementCaixa(2, false)
      return
    }

    if (
      caixa?.departamental.nuCaixa + 1 <
      Number(form.caixaDepartamental.numeroCaixa) ||
      caixa?.departamental.nuCaixa > Number(form.caixaDepartamental.numeroCaixa)
    ) {
      showModalAbrirCaixa(TipoCaixa.Departamental)
      return
    }

    if (
      caixa?.departamental.nuCaixa ===
      Number(form.caixaDepartamental.numeroCaixa)
    ) {
      setForm((prevState: ICaixaForm) => ({
        ...prevState,
        caixaDepartamental: {
          ...prevState.caixaDepartamental,
          numeroCaixa: (caixa?.departamental.nuCaixa + 1).toString()
        }
      }))
      incrementValue.current = 1
    }

    incrementCaixa(TipoCaixa.Departamental, true)
  }

  useEffect(() => {
    if (firstMount && form.currentCaixa === TipoCaixa.Departamental) {
      getCaixa()
      setFirstMount(false)
    }
  }, [contract])

  useEffect(() => {
    if (contract && firstMount && form.currentCaixa === TipoCaixa.Externa) {
      getCaixa(contract?.centroPlantaId)
      setFirstMount(false)
    }
  }, [contract])

  const getCaixa = (idCentroPlanta?: number) => {
    setLoading('loading')

    CaixaServices()
      .findAtual(idCentroPlanta)
      .then((response) => {
        if (response.status === 200) {
          const { data: caixa } = response

          if (caixa) {
            setCaixa((oldData) => ({
              ...oldData,
              [idCentroPlanta ? 'departamental' : 'externa']: caixa
            }))
          }

          setForm((prevState) => ({
            ...prevState,
            [caixa.idTipoCaixa === TipoCaixa.Externa
              ? 'caixaExterna'
              : 'caixaDepartamental']: {
              idCaixa: caixa.idCaixa,
              idTipoCaixa: caixa.idTipoCaixa,
              numeroCaixa: caixa.nuCaixa
            }
          }))
        } else if (response.status === 204) {
          setForm((prevState) => ({
            ...prevState,
            caixaDepartamental: {
              idCaixa: 0,
              idTipoCaixa: TipoCaixa.Departamental,
              numeroCaixa: ''
            },
            caixaExterna: {
              idCaixa: 0,
              idTipoCaixa: TipoCaixa.Externa,
              numeroCaixa: ''
            },
            currentCaixa: TipoCaixa.Externa
          }))
        } else {
          handleCaixaError()
        }

        setLoading('idle')
      })
      .catch(() => {
        setLoading('idle')
        handleCaixaError()
      })
  }

  useEffect(() => {
    getDocumentoCaixaById()
  }, [])

  return (
    <>
      <Grid
        gridCols="1fr 2fr 2fr 1fr 2fr"
        gridGap={`0 ${SPACING.spacing15}`}
        margin={`${SPACING.spacing15} 0 ${SPACING.spacing15} 0`}
      >
        <Input
          label="Contrato"
          placeholder="N°"
          value={form.cdContrato}
          name="cdContrato"
          disabled
          maxLength={50}
        />
        <Input
          value={form.nmEmpresa}
          label="Empresa"
          placeholder="Empresa"
          disabled
        />
        <Input
          label="Centro"
          value={form.nmCentro}
          placeholder="Centro"
          disabled
        />
        <Input
          label="CPF/CNPJ/Cód.Fornecedor"
          placeholder="Nº"
          value={form.cnpj && FORMAT.formatCdDocto(form.cnpj)}
          disabled
        />

        <Input
          label="Fornecedor"
          value={form.nmFornecedor}
          placeholder="Fornecedor"
          disabled
        />
      </Grid>
      <Span
        text="ARQUIVO"
        marginTop={SPACING.spacing20}
        fontWeight={FONTS.bold}
        fontSize={TYPOGRAPHY.bodySize16}
      />
      <Row alignItems="center" marginTop={SPACING.spacing20}>
        <Picker
          label="Competência"
          views={['year', 'month']}
          marginRight={SPACING.spacing15}
          value={form.competencia}
          defaultValue={form.competencia?.toString() || moment().toString()}
          onChange={(date) =>
            setForm((prevState) => ({
              ...prevState,
              competencia: moment(date).format('yyyyMM')
            }))
          }
          width="150px"
          type="monthAndYear"
        />
        <Col>
          <Span text="Caixa Externo" />
          <CheckBox
            checked={form.currentCaixa == TipoCaixa.Externa}
            name="caixaExterno"
            onChange={handleSelectCheckbox}
          />
        </Col>
        <InputMask
          marginTop={SPACING.spacing20}
          marginLeft="-3rem"
          disabled={form.currentCaixa == TipoCaixa.Departamental}
          value={form.caixaExterna.numeroCaixa}
          name="caixaExterna.numeroCaixa"
          formValue="caixaExterna.numeroCaixa"
          onChange={handleOnChange}
          type="number"
          maxLength={8}
          onBlur={
            caixa?.externa.nuCaixa + 1 <
              Number(form.caixaExterna.numeroCaixa) ||
              caixa?.externa.nuCaixa > Number(form.caixaExterna.numeroCaixa)
              ? gerarCaixaExterno
              : null
          }
        />
        <Button
          text="GERAR CAIXA"
          disabled={form.currentCaixa === TipoCaixa.Departamental}
          marginLeft={SPACING.spacing10}
          onClick={gerarCaixaExterno}
        />
      </Row>

      <Row marginTop={SPACING.spacing20} marginLeft="165px">
        <Col>
          <Span text="Caixa Departamental" />
          <CheckBox
            checked={form.currentCaixa === TipoCaixa.Departamental}
            name="caixaDepartamental"
            onChange={handleSelectCheckbox}
          />
        </Col>
        <InputMask
          marginTop={SPACING.spacing20}
          marginLeft="-5.75rem"
          value={form.caixaDepartamental.numeroCaixa}
          disabled={form.currentCaixa === TipoCaixa.Externa}
          name="caixaDepartamental.numeroCaixa"
          formValue="caixaDepartamental.numeroCaixa"
          onChange={handleOnChange}
          type="number"
          maxLength={8}
          onBlur={
            caixa?.departamental.nuCaixa + 1 <
              Number(form.caixaDepartamental.numeroCaixa) ||
              caixa?.departamental.nuCaixa >
              Number(form.caixaDepartamental.numeroCaixa)
              ? gerarCaixaDepartamental
              : null
          }
        />
        <Button
          text="GERAR CAIXA"
          marginLeft={SPACING.spacing10}
          marginTop={SPACING.spacing10}
          disabled={form.currentCaixa === TipoCaixa.Externa}
          onClick={gerarCaixaDepartamental}
        />
      </Row>

      <Grid
        gridCols="4fr 4fr"
        gridGap={`0 ${SPACING.spacing15}`}
        margin={`${SPACING.spacing50} 0 ${SPACING.spacing30} 0`}
      >
        <InputMask
          label="Descrição do Conteúdo da caixa"
          placeholder="Descrição"
          name="descricao"
          formValue="descricao"
          value={form.descricao}
          onChange={handleOnChange}
          maxLength={200}
          width="900px"
        />
      </Grid>

      <Row marginBottom={SPACING.spacing15} justifyContent="flex-end">
        <Button text="Limpar" variantButton="text" onClick={clearForm} />
        <Button
          text="Voltar"
          marginRight="7px"
          variantButton="outlined"
          onClick={() => handleChange(null, 0)}
        />
        <Button text="Salvar" onClick={onSubmit} />
      </Row>
    </>
  )
}

export default EditNewDocumentation
